/**
 * Visibilità di un campo (CF06) — CL03.
 *
 * Tre modalità, **esclusive**: il processo intero, un insieme di corsie, un
 * insieme di task. Esclusive perche' sono tre insiemi di id diversi: una corsia
 * non e' un task, e mescolarli darebbe una selezione che il server non sa
 * leggere.
 *
 * Non c'e' nessuna regola qui dentro: `changeScope`, `setEntryAccess`,
 * `removeEntry` e `missingReferences` stanno in `visibilityImpact.ts`, dove si
 * verificano senza un browser.
 *
 * L'unica decisione che vive in questo file: **cambiare modalita' chiede
 * conferma**. Passare a "processo" perde la selezione, e cambiare fra corsie e
 * task pure, perche' gli id appartengono a insiemi diversi. Perderla in
 * silenzio sarebbe irreversibile dentro una bozza che il progettista crede di
 * poter annullare.
 */

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import React, { useEffect, useMemo, useState } from "react";
import DialogHeader from "components/dialogs/DialogHeader";
import { getTranslation } from "common";
import { useTranslations } from "hooks";
import type { ElementAccess, FieldDefinition, FieldVisibility } from "model/CustomFields";
import {
  VISIBILITY_SCOPES,
  changeScope,
  defaultVisibility,
  entriesOf,
  groupChoices,
  missingReferences,
  removeEntry,
  searchChoiceGroups,
  scopeOf,
  setEntryAccess,
  type ElementChoice,
} from "./visibilityImpact";
import { isServerAssigned } from "./fieldTypes";
import { translateMessage } from "./messages";

interface FieldVisibilityDialogProps {
  open: boolean;
  onClose: () => void;
  field: FieldDefinition | null;
  /** Le corsie del modello. */
  lanes: readonly ElementChoice[];
  /** I task del modello. */
  tasks: readonly ElementChoice[];
  onSave: (visibility: FieldVisibility) => void;
}

const FieldVisibilityDialog: React.FC<FieldVisibilityDialogProps> = ({
  open,
  onClose,
  field,
  lanes,
  tasks,
  onSave,
}) => {
  const translations = useTranslations();
  const [visibility, setVisibility] = useState<FieldVisibility>(defaultVisibility());
  const [pendingScope, setPendingScope] = useState<FieldVisibility["scope"] | null>(null);
  const [search, setSearch] = useState("");
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);

  useEffect(() => {
    setVisibility(field ? field.visibility : defaultVisibility());
    setPendingScope(null);
    setSearch("");
    setExpandedGroup(null);
  }, [field, open]);

  const scope = scopeOf(visibility);

  /** Gli elementi che la modalita' corrente rende selezionabili. */
  const available = useMemo(
    () => (scope === "lanes" ? lanes : scope === "tasks" ? tasks : []),
    [scope, lanes, tasks]
  );

  // Gli id selezionati che il modello non ha piu': si mostrano, non si
  // nascondono. Quel campo non sarebbe compilabile da nessuno.
  const missing = useMemo(
    () => missingReferences(visibility, available.map((e) => e.elementId)),
    [visibility, available]
  );

  const selected = entriesOf(visibility);
  const groups = scope === "tasks" ? searchChoiceGroups(available, search) : groupChoices(available);
  const isSelected = (elementId: string) =>
    selected.find((entry) => entry.elementId === elementId);

  if (!field) return null;

  /**
   * Le voci che una modalita' rende selezionabili, per la modalita' **di
   * destinazione**: cambiando modo la selezione si ricostruisce da qui.
   */
  const availableFor = (target: FieldVisibility["scope"]): readonly ElementChoice[] =>
    target === "lanes" ? lanes : target === "tasks" ? tasks : [];

  // L'accesso con cui nascono le voci (e a cui torna lo scope di processo):
  // i tipi che il server assegna non sono mai scrivibili, e offrirli editabili
  // sarebbe offrire un accesso che il server rifiuta comunque (policy.py).
  const defaultAccess: ElementAccess["access"] = isServerAssigned(field.type)
    ? "readonly"
    : "editable";

  /** Cambia modalita', chiedendo conferma solo se c'e' qualcosa da perdere. */
  const requestScope = (next: FieldVisibility["scope"]) => {
    const { visibility: changed, losesSelection } = changeScope(
      visibility,
      next,
      availableFor(next),
      defaultAccess
    );
    if (losesSelection) {
      setPendingScope(next);
      return;
    }
    setVisibility(changed);
  };

  const confirmScopeChange = () => {
    if (pendingScope) {
      setVisibility(
        changeScope(visibility, pendingScope, availableFor(pendingScope), defaultAccess)
          .visibility
      );
    }
    setPendingScope(null);
  };

  const accessFor = (elementId: string): ElementAccess["access"] =>
    isSelected(elementId)?.access ?? "readonly";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth id="field-visibility-dialog">
      <DialogHeader sx={{ px: 4 }} onClose={onClose}>
        {/* `{field}` è un segnaposto del catalogo: senza comporlo, il titolo
            diceva "Visibilità di {field}" alla lettera. */}
        {translateMessage(
          getTranslation(translations, "custom_fields.visibility.title"),
          { field: field.label }
        )}
      </DialogHeader>
      <DialogContent>
        <Stack gap={2} pt={1}>
          <RadioGroup
            value={scope}
            onChange={(e) => requestScope(e.target.value as FieldVisibility["scope"])}
          >
            {VISIBILITY_SCOPES.map((candidate) => (
              <FormControlLabel
                key={candidate}
                value={candidate}
                data-testid={`cf-visibility-scope-${candidate}`}
                control={<Radio size="small" />}
                label={getTranslation(
                  translations,
                  `custom_fields.visibility.scope.${candidate}`
                )}
              />
            ))}
          </RadioGroup>

          {scope === "process" ? (
            <TextField
              select
              size="small"
              fullWidth
              label={getTranslation(translations, "custom_fields.visibility.access")}
              SelectProps={
                { "data-testid": "cf-visibility-process-access" } as never
              }
              value={visibility.scope === "process" ? visibility.access : "editable"}
              onChange={(e) =>
                setVisibility({
                  scope: "process",
                  access: e.target.value as ElementAccess["access"],
                })
              }
            >
              <MenuItem value="editable" disabled={defaultAccess === "readonly"}>
                {getTranslation(translations, "custom_fields.visibility.access.editable")}
              </MenuItem>
              <MenuItem value="readonly">
                {getTranslation(translations, "custom_fields.visibility.access.readonly")}
              </MenuItem>
            </TextField>
          ) : (
            <Stack gap={2} data-testid="cf-visibility-entries">
              {scope === "tasks" && (
                <TextField
                  size="small"
                  fullWidth
                  label={getTranslation(translations, "generic.search.activity")}
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  inputProps={{ "data-testid": "cf-visibility-search" }}
                />
              )}
              {available.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  {getTranslation(translations, "custom_fields.visibility.nothing_selectable")}
                </Typography>
              )}

              {scope === "tasks" && available.length > 0 && groups.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  {getTranslation(translations, "custom_fields.visibility.nothing_selectable")}
                </Typography>
              )}

              {groups.map((group) => {
                const groupKey = group.id ?? "none";
                const rows = (
                  <Stack gap={1}>
                  {/* L'etichetta del pool si mostra solo dove un pool c'è:
                      separare ciò che sta già da solo sarebbe rumore. */}
                  {scope === "lanes" && group.label && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 600, letterSpacing: 0.4 }}
                    >
                      {group.label}
                    </Typography>
                  )}

                  {group.choices.map((element) => {
                    const entry = isSelected(element.elementId);

                    return (
                      <Stack
                        key={element.elementId}
                        direction="row"
                        spacing={1}
                        alignItems="center"
                      >
                        <Checkbox
                          size="small"
                          inputProps={
                            {
                              "data-testid": `cf-visibility-select-${element.elementId}`,
                            } as React.InputHTMLAttributes<HTMLInputElement>
                          }
                          checked={Boolean(entry)}
                          onChange={(e, checked) =>
                            setVisibility(
                              checked
                                ? setEntryAccess(visibility, element.elementId, defaultAccess)
                                : removeEntry(visibility, element.elementId)
                            )
                          }
                        />
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {element.label}
                        </Typography>
                        <TextField
                          select
                          size="small"
                          sx={{ width: 150 }}
                          // L'accesso e' per riferimento: una corsia puo' essere in
                          // sola lettura mentre un'altra e' editabile.
                          disabled={!entry}
                          SelectProps={
                            {
                              "data-testid": `cf-visibility-access-${element.elementId}`,
                            } as never
                          }
                          value={accessFor(element.elementId)}
                          onChange={(e) =>
                            setVisibility(
                              setEntryAccess(
                                visibility,
                                element.elementId,
                                e.target.value as ElementAccess["access"]
                              )
                            )
                          }
                        >
                          <MenuItem
                            value="editable"
                            disabled={defaultAccess === "readonly"}
                          >
                            {getTranslation(
                              translations,
                              "custom_fields.visibility.access.editable"
                            )}
                          </MenuItem>
                          <MenuItem value="readonly">
                            {getTranslation(
                              translations,
                              "custom_fields.visibility.access.readonly"
                            )}
                          </MenuItem>
                        </TextField>
                      </Stack>
                    );
                  })}
                  </Stack>
                );
                if (scope === "lanes") {
                  return (
                    <Stack
                      key={groupKey}
                      data-testid={`cf-visibility-group-${groupKey}`}
                    >
                      {rows}
                    </Stack>
                  );
                }
                return (
                  <Accordion
                    key={groupKey}
                    data-testid={`cf-visibility-group-${groupKey}`}
                    expanded={Boolean(search.trim()) || expandedGroup === groupKey}
                    onChange={(_, expanded) => setExpandedGroup(expanded ? groupKey : null)}
                    disableGutters
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      data-testid={`cf-visibility-group-toggle-${groupKey}`}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {getTranslation(translations, "pm.swimlanename.label.caption")}{" "}
                        {group.id ?? "—"}
                        {group.label ? ` · ${group.label}` : ""}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>{rows}</AccordionDetails>
                  </Accordion>
                );
              })}
            </Stack>
          )}

          {missing.length > 0 && (
            <Alert severity="warning" data-testid="cf-visibility-missing">
              {getTranslation(translations, "custom_fields.visibility.missing_references")}
              {": "}
              {missing.join(", ")}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} data-testid="cf-visibility-cancel">
          {getTranslation(translations, "custom_fields.action.cancel")}
        </Button>
        <Button onClick={() => onSave(visibility)} data-testid="cf-visibility-save">
          {getTranslation(translations, "pm.button.save")}
        </Button>
      </DialogActions>

      <Dialog open={pendingScope !== null} onClose={() => setPendingScope(null)}>
        <DialogHeader sx={{ px: 4 }} onClose={() => setPendingScope(null)}>
          {getTranslation(translations, "custom_fields.visibility.lose_selection")}
        </DialogHeader>
        <DialogActions>
          <Button onClick={() => setPendingScope(null)}>
            {getTranslation(translations, "custom_fields.action.keep_editing")}
          </Button>
          <Button
            onClick={confirmScopeChange}
            color="error"
            data-testid="cf-visibility-lose-confirm"
          >
            {getTranslation(translations, "custom_fields.action.discard")}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default FieldVisibilityDialog;
